/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateSetter, useId, useState } from 'react';
import { AxiosError } from 'axios';
import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';

import { useWatch } from 'hooks';
import { useToast } from 'providers/Toast';

import { getFirst } from 'utils/array';
import { isDeepEqual } from 'utils/equal';

import { MessageType, PayloadByType } from 'providers/RealTime/types';
import { useRealTimeSubscription } from 'providers/RealTime';

import { IsUndefined } from 'types';
import { LoadResult, LoadParams, LoadConfig, OptionalArguments } from './types';
import { useReloadRef } from './reloadRef.hook';
import { cascadeEval } from 'utils/conditions';

export type ParamConfig<
  TargetLoaders extends LoadConfig,
  Module extends keyof TargetLoaders,
  Target extends keyof TargetLoaders[Module],
> =
  IsUndefined<LoadParams<TargetLoaders, Module, Target>> extends true
    ? {
        params?: LoadParams<TargetLoaders, Module, Target>;
      }
    : {
        params: LoadParams<TargetLoaders, Module, Target>;
      };

export type LoadOptions<
  TargetLoaders extends LoadConfig,
  Module extends keyof TargetLoaders,
  Target extends keyof TargetLoaders[Module],
  DefaultValue extends LoadResult<TargetLoaders, Module, Target> | undefined = undefined,
  SubType extends MessageType = MessageType,
  //
  //
  __ENTITY__ = LoadResult<TargetLoaders, Module, Target>,
> = ParamConfig<TargetLoaders, Module, Target> & {
  blockLoad?: boolean;

  onLoad?: (
    entity: __ENTITY__,
    params: ParamConfig<TargetLoaders, Module, Target>['params'],
  ) => void;

  onDataChange?: (entity: __ENTITY__) => void;

  parser?: (entity: __ENTITY__) => __ENTITY__;

  onError?: (err: AxiosError) => void;

  defaultValue?: DefaultValue;

  refreshMilSeconds?: number;
  refreshMinutes?: number;

  /**
   * Do not match this query
   * with possibly the same used anywhere else in the app
   */
  unique?: boolean;

  /**
   * Use with discretion,
   * its usually not recommended to have this effect
   *
   * `undefined` values do not work
   */
  reloadOnChangeRef?: any;

  realTimeSubscription?: {
    type: SubType;

    overwrite?: boolean;

    onMessage?: (update: PayloadByType[SubType], current: __ENTITY__) => __ENTITY__;

    shouldReload?: (update: PayloadByType[SubType], current: __ENTITY__) => boolean;
  };
};

export interface UseEntityLoad<
  TargetLoaders extends LoadConfig,
  Module extends keyof TargetLoaders,
  Target extends keyof TargetLoaders[Module],
  DefaultValue,
> {
  data: DefaultValue extends undefined
    ? LoadResult<TargetLoaders, Module, Target> | undefined
    : LoadResult<TargetLoaders, Module, Target>;

  loading: boolean;
  fetching: boolean;
  error: boolean;
  errorOrLoading: boolean;
  loadingThroughCache: boolean;

  load: (
    ...params: OptionalArguments<ParamConfig<TargetLoaders, Module, Target>['params']>
  ) => Promise<LoadResult<TargetLoaders, Module, Target> | undefined>;
  retry: () => Promise<void>;

  clear: () => void;

  overwrite: StateSetter<LoadResult<TargetLoaders, Module, Target>>;
}

export function createUseLoad<TargetLoaders extends LoadConfig>(entityLoaders: TargetLoaders) {
  return function useLoad<
    Module extends keyof TargetLoaders,
    Target extends keyof TargetLoaders[Module],
    DefaultValue extends LoadResult<TargetLoaders, Module, Target> | undefined,
  >(
    realm: Module,
    subRealm: Target,
    ...options: OptionalArguments<
      LoadOptions<TargetLoaders, Module, Target, DefaultValue>,
      LoadParams<TargetLoaders, Module, Target>
    >
  ): UseEntityLoad<TargetLoaders, Module, Target, DefaultValue> {
    type Entity = LoadResult<TargetLoaders, Module, Target>;
    type Result = UseEntityLoad<TargetLoaders, Module, Target, DefaultValue>;

    const id = useId();
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    const {
      blockLoad,
      params = {},
      onLoad,
      parser,
      defaultValue,
      refreshMilSeconds,
      refreshMinutes,
      onError,
      reloadOnChangeRef,
      onDataChange,

      realTimeSubscription,

      unique,
    } = getFirst(options) ?? {};

    const loadConfig = entityLoaders[realm][subRealm];

    /**
     * Stats with module.target so we can easily invalidate!
     *
     * We do not add `reloadOnChangeRef` here because its handled on `useReloadRef`
     */
    const queryKey: QueryKey = unique ? [realm, subRealm, params, id] : [realm, subRealm, params];

    const query = useQuery<Entity, AxiosError<any>>({
      queryKey,
      retry: false,

      /**
       * Warning:
       * React query **DOES NOT** trigger the query
       * if we flip this after mount
       *
       * We handle it inside the `useReloadRef`
       */
      enabled: !blockLoad,

      placeholderData: defaultValue,

      /*
        This means we'll always have data to show,
        but will immediately fetch in the bg to update it
      */
      staleTime: Infinity,
      refetchOnMount: 'always',

      refetchOnWindowFocus: false,
      refetchInterval:
        refreshMilSeconds || (refreshMinutes ? refreshMinutes * 1000 * 60 : undefined),

      queryFn: () => loadConfig.load(params),

      onSuccess: (data) => {
        onLoad?.(data, params as any);
      },

      select: parser || loadConfig.defaultParser,

      onError: (error) => {
        const msg = error.response?.data?.message;

        if (msg) addToast({ type: 'error', title: msg });

        onError?.(error);
      },
    });

    const load = async (newParams?: LoadParams<TargetLoaders, Module, Target>) => {
      const { load, defaultParser } = loadConfig;

      const data = await load(newParams ?? params);

      const actualParser = parser ?? defaultParser;

      const actualData = actualParser?.(data) ?? data;

      return actualData;
    };

    const refData = query.data || defaultValue;

    const [controlData, setControlData] = useState<Entity | undefined>(refData);

    /**
     * Ensures we do not lag behind on `loading` if we may be
     * controlling the state elsewhere, we want to ensure the first even data
     * passing is done correctly. After that, no need for the extra
     * state changes...
     *
     * We only care to see if data is not here to prevent hard crashes with loading:false
     * and undefined values
     */
    const shouldControlExposure = (!!onLoad || !!onDataChange) && !controlData;

    const exposedData = shouldControlExposure ? controlData : refData;

    const dataIsDefault = isDeepEqual(exposedData, defaultValue);

    /**
     * Reminder:
     *    isLoading = true => when no queryFn has succeed
     *    isFetching = true => anytime the queryFn is executing
     */
    const loadingState = blockLoad
      ? false
      : dataIsDefault
        ? query.isLoading || query.isFetching
        : query.isLoading;

    /**
     * You can change
     *
     * query.data && !controlData
     *
     * to:
     *
     * query.data !== controlData
     *
     * to ensure full control, but thats not really required here
     */
    const loading =
      shouldControlExposure && !loadingState && query.data && !controlData ? true : loadingState;

    const overwrite: Result['overwrite'] = (updater) =>
      queryClient.setQueryData(queryKey, updater as any);

    useWatch(query.data, (data) => {
      /**
       * We do this to prevent the 1 cycle where query.data is present
       * but onDataChange/onLoad have not been called
       */
      if (shouldControlExposure) setControlData(data);

      data && onDataChange && !isDeepEqual(data, defaultValue) && onDataChange(data as any);
    });

    useReloadRef({
      enabled: !blockLoad,
      fetch: query.refetch,
      reloadRef: reloadOnChangeRef,
    });

    // -------------------------- REALTIME SUBSCRIPTION ----------------------------------
    // Revise after fix

    useRealTimeSubscription(realTimeSubscription?.type ?? null, (payload) => {
      const saveOnTop = realTimeSubscription?.overwrite ?? false;

      overwrite((data) => {
        const updated = cascadeEval([
          {
            is: realTimeSubscription?.onMessage,
            then: () => realTimeSubscription?.onMessage?.(payload, data),
          },
          { is: saveOnTop, then: payload as Entity },
          { is: true, then: data },
        ]);

        return updated as Entity;
      });

      if (realTimeSubscription?.shouldReload?.(payload, query.data!)) query.refetch();
    });

    // -------------------------- REALTIME SUBSCRIPTION ----------------------------------

    return {
      data: exposedData as Result['data'],

      loading,
      errorOrLoading: loading || query.isError,
      error: query.isError,
      loadingThroughCache: query.isFetching && !dataIsDefault,
      fetching: query.isFetching,

      overwrite: (updater) => queryClient.setQueryData(queryKey, updater as any),

      retry: async () => {
        await query.refetch();
      },

      clear: () => queryClient.removeQueries(queryKey, { exact: true }),

      load,
    };
  };
}
